import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  type SelectChangeEvent,
} from '@mui/material';
import {
  AttachMoney,
  Add,
  Edit,
  Delete,
  Close,
  CloudDownload,
  ExpandMore,
  Code,
  Business,
  GroupsRounded,
  Work,
  Psychology,
  LocalLibraryRounded,
  Language,
  Quiz,
  Campaign,
  School,
  Assessment,
  People,
  Star,
} from '@mui/icons-material';
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/api';

interface SubCategoryLevel {
  name: string;
  papers: string[];
}

interface SubCategoryItem {
  name: string;
  levels: SubCategoryLevel[];
}

interface ServiceCategoryRecord {
  _id: string;
  title: string;
  category: string;
  description: string;
  color: string;
  iconKey: string;
  subCategories: SubCategoryItem[];
  requiresStudyDetails: boolean;
  requiresExperience: boolean;
  amount: number;
  currency: string;
  commissionPercentage: number;
  isActive: boolean;
}

interface FormState {
  title: string;
  category: string;
  description: string;
  color: string;
  iconKey: string;
  subCategories: SubCategoryItem[];
  requiresStudyDetails: boolean;
  requiresExperience: boolean;
  amount: string;
  currency: string;
  commissionPercentage: string;
  isActive: boolean;
}

const ICON_OPTIONS: { key: string; label: string; Icon: React.ComponentType }[] = [
  { key: 'code', label: 'Code', Icon: Code },
  { key: 'business', label: 'Business', Icon: Business },
  { key: 'groups', label: 'Groups', Icon: GroupsRounded },
  { key: 'work', label: 'Work', Icon: Work },
  { key: 'psychology', label: 'Psychology', Icon: Psychology },
  { key: 'library', label: 'Library', Icon: LocalLibraryRounded },
  { key: 'language', label: 'Language', Icon: Language },
  { key: 'quiz', label: 'Quiz', Icon: Quiz },
  { key: 'campaign', label: 'Campaign', Icon: Campaign },
  { key: 'school', label: 'School', Icon: School },
  { key: 'assessment', label: 'Assessment', Icon: Assessment },
  { key: 'people', label: 'People', Icon: People },
  { key: 'star', label: 'Star', Icon: Star },
];

const iconForKey = (key: string) => ICON_OPTIONS.find(o => o.key === key)?.Icon || Star;

const emptyForm: FormState = {
  title: '',
  category: '',
  description: '',
  color: '#2196f3',
  iconKey: 'star',
  subCategories: [],
  requiresStudyDetails: true,
  requiresExperience: true,
  amount: '',
  currency: 'RWF',
  commissionPercentage: '',
  isActive: true,
};

const flatSubCategories = (names: string[]): SubCategoryItem[] => names.map(name => ({ name, levels: [] }));

const KASNEB_CPA_LEVELS: SubCategoryLevel[] = [
  {
    name: 'Foundation Level',
    papers: [
      'CA11: Financial Accounting',
      'CA12: Communication Skills',
      'CA13: Introduction to Law and Governance',
      'CA14: Economics',
      'CA15: Quantitative Analysis',
      'CA16: Information Communication Technology',
    ],
  },
  {
    name: 'Intermediate Level',
    papers: [
      'CA21: Company Law',
      'CA22: Financial Management',
      'CA23: Financial Reporting and Analysis',
      'CA24: Auditing and Assurance',
      'CA25: Management Accounting',
      'CA26: Public Finance and Taxation',
    ],
  },
  {
    name: 'Advanced Level - Compulsory Papers',
    papers: [
      'CA31: Leadership and Management',
      'CA32: Advanced Financial Reporting and Analysis',
      'CA33: Advanced Financial Management',
      'CA34S3: Advanced Management Accounting',
    ],
  },
  {
    name: 'Advanced Level - Specialisation Papers',
    papers: [
      'CA34S1: Advanced Taxation',
      'CA34S2: Advanced Auditing and Assurance',
      'CA34S4: Advanced Public Financial Management',
    ],
  },
  {
    name: 'Advanced Level - Practical / Professional Components',
    papers: [
      'CA35P: Business Data Analytics (Practical Paper)',
      'CA36WE: Workshop on Ethics',
      'CA37WP: Workshop on Work Simulation',
    ],
  },
];

const ICPAR_CPA_LEVELS: SubCategoryLevel[] = [
  {
    name: 'Technical Level',
    papers: ['Financial Accounting', 'Management Accounting', 'Taxation', 'Audit & Assurance'],
  },
  {
    name: 'Operational Level',
    papers: ['Law, Ethics & Governance', 'Digital Finance', 'Financial Management', 'Financial Reporting', 'Advanced Taxation'],
  },
  {
    name: 'Strategic Level',
    papers: [
      'Advanced Audit & Assurance',
      'Strategic Management',
      'Advanced Financial Reporting',
      'Advanced Public Financial Management (public sector pathway)',
      'Managing Performance in the Public Sector (public sector pathway)',
      'Managing Business Performance (private sector pathway)',
      'Advanced Financial Management (private sector pathway)',
    ],
  },
  {
    name: 'Professional Level',
    papers: ['Test of Professional Competence', 'Choose Private Sector', 'Choose Public Sector'],
  },
];

const DEFAULT_CATEGORIES: Omit<FormState, 'amount' | 'currency' | 'commissionPercentage' | 'isActive'>[] = [
  {
    title: 'Tech Coaching',
    category: 'Technology',
    description: 'AI, Data Analytics, Web & App Development, Python, Software Development',
    color: '#2196f3',
    iconKey: 'code',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Artificial Intelligence & Machine Learning',
      'Web Development (Frontend & Backend)',
      'Mobile App Development',
      'Cloud Computing & DevOps',
      'Cybersecurity',
      'Python Programming',
      'Software Development',
      'Database Management',
      'UI/UX Design',
    ]),
  },
  {
    title: 'Business Coaching',
    category: 'Business',
    description: 'Entrepreneurship, Management, Marketing, Finance, Leadership',
    color: '#ff9800',
    iconKey: 'business',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Business Administration',
      'Entrepreneurship & Startups',
      'Digital Marketing',
      'Financial Management',
      'Project Management',
      'Human Resources',
      'Supply Chain Management',
      'Business Analytics',
      'Strategic Management',
      'International Business',
    ]),
  },
  {
    title: 'Accounting & Tax Advisory',
    category: 'Finance',
    description: 'Financial Accounting, Tax Planning, Bookkeeping, Financial Advisory Services',
    color: '#ff9800',
    iconKey: 'business',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Financial Accounting & Reporting',
      'Tax Planning & Compliance',
      'Bookkeeping & Financial Analysis',
      'Financial Advisory Services',
      'Auditing & Internal Controls',
      'International Accounting Standards',
      'Corporate Finance',
      'Investment Analysis',
      'Risk Management & Compliance',
      'Forensic Accounting',
      'Tax Advisory & Consulting',
    ]),
  },
  {
    title: 'Human Resource Management',
    category: 'Business',
    description: 'HR Strategy, Talent Management, Employee Relations, Performance Management',
    color: '#9c27b0',
    iconKey: 'groups',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'HR Strategy & Workforce Planning',
      'Talent Acquisition & Recruitment',
      'Employee Relations & Engagement',
      'Performance Management Systems',
      'Compensation & Benefits Administration',
      'HR Analytics & Metrics',
      'Training & Development',
      'Organizational Development',
      'Employee Wellness Programs',
      'Labor Law & Compliance',
      'Diversity & Inclusion',
      'HR Information Systems',
    ]),
  },
  {
    title: 'Professional Coaching',
    category: 'Professional Coaching',
    description: 'Data Analytics, CPA, ACCA, PMP, Leadership and Management, and more',
    color: '#ff6b6b',
    iconKey: 'work',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: [
      { name: 'Data Analytics Coaching', levels: [] },
      { name: 'CPA (Certified Public Accountant) ICPAR Coaching', levels: ICPAR_CPA_LEVELS },
      { name: 'CPA (Certified Public Accountant) KASNEB Coaching', levels: KASNEB_CPA_LEVELS },
      { name: 'ACCA (Association of Chartered Certified Accountants) Coaching', levels: [] },
      { name: 'PMP (Project Management Professional) Coaching', levels: [] },
      { name: 'Leadership Development Coaching', levels: [] },
    ],
  },
  {
    title: 'Parenting & Mental Health Coaching',
    category: 'Wellness',
    description: 'Parenting Support, Mental Health Wellness, Emotional Intelligence, Family Counseling',
    color: '#00bcd4',
    iconKey: 'psychology',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Parenting Skills & Child Development',
      'Mental Health & Wellness Support',
      'Emotional Intelligence Training',
      'Family Counseling & Therapy',
      'Stress Management Techniques',
      'Work-Life Balance Strategies',
      'Child Psychology & Development',
      'Adolescent Mental Health',
      'Relationship Counseling',
      'Self-Care & Mindfulness',
      'Parenting for Special Needs',
      'Family Communication Skills',
    ]),
  },
  {
    title: 'Academic Coaching',
    category: 'Academic Coaching',
    description: 'Mathematics, Sciences, Languages, Test Preparation, Study Skills',
    color: '#4caf50',
    iconKey: 'library',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Mathematics (All Levels)',
      'Physics & Chemistry',
      'Biology & Life Sciences',
      'English Language & Literature',
      'French & Other Languages',
      'Test Preparation (SAT, GRE, etc.)',
      'Study Skills & Time Management',
      'Research & Writing',
      'Critical Thinking',
      'Academic Planning',
    ]),
  },
  {
    title: 'Career Development',
    category: 'Career Development',
    description: 'Resume Building, Interview Skills, Career Planning, Job Search Strategies',
    color: '#9c27b0',
    iconKey: 'work',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Resume & CV Writing',
      'Interview Preparation',
      'Career Planning & Counseling',
      'Job Search Strategies',
      'Networking Skills',
      'Professional Communication',
      'Leadership Development',
      'Public Speaking',
      'Workplace Skills',
      'Career Transition',
      'Career Guidance',
      'Job Exams and Assessments',
    ]),
  },
  {
    title: 'Language Coaching',
    category: 'Languages',
    description: 'Chinese, German, Arabic, Kinyarwanda, French, Kiswahili, English, and more',
    color: '#ff5722',
    iconKey: 'language',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories(['Chinese', 'German', 'Arabic', 'Kinyarwanda', 'French', 'Kiswahili', 'English', 'TOEFL Preparation']),
  },
  {
    title: 'E-Exams',
    category: 'Examinations',
    description: 'Online certification exams, practice tests, and proctored professional assessments',
    color: '#009688',
    iconKey: 'quiz',
    requiresStudyDetails: true,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Professional Certification Exams (CPA, ACCA, PMP)',
      'Academic & School Exams',
      'Practice & Mock Exams',
      'Proctored Remote Exams',
      'Psychometric & Aptitude Tests',
      'Language Proficiency Exams',
      'IT & Technical Certification Exams',
      'Custom Institutional Exams',
    ]),
  },
  {
    title: 'Digital Marketing',
    category: 'Marketing',
    description: 'SEO, Social Media, Content Marketing, Email Marketing, PPC and Digital Strategy',
    color: '#e91e63',
    iconKey: 'campaign',
    requiresStudyDetails: false,
    requiresExperience: true,
    subCategories: flatSubCategories([
      'Search Engine Optimization (SEO)',
      'Social Media Marketing',
      'Content Marketing & Strategy',
      'Email Marketing & Automation',
      'Pay-Per-Click (PPC) & Paid Advertising',
      'Digital Marketing Strategy & Analytics',
      'Brand Management & Online Reputation',
      'E-commerce & Affiliate Marketing',
      'Business Documentary Production',
      'Ads & Paid Advertising Campaigns',
    ]),
  },
];

const commissionPreview = (amount: string, commissionPercentage: string) => {
  const a = parseFloat(amount);
  const c = parseFloat(commissionPercentage);
  if (isNaN(a) || isNaN(c)) return null;
  return Math.round(((a * c) / 100) * 100) / 100;
};

const PaperListEditor: React.FC<{ papers: string[]; onChange: (next: string[]) => void }> = ({ papers, onChange }) => {
  const [draft, setDraft] = useState('');

  const addPaper = () => {
    const trimmed = draft.trim();
    if (!trimmed || papers.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...papers, trimmed]);
    setDraft('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Add Exam Paper / Topic"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addPaper();
            }
          }}
        />
        <Button size="small" variant="outlined" onClick={addPaper}>Add</Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {papers.map(paper => (
          <Chip key={paper} label={paper} size="small" onDelete={() => onChange(papers.filter(p => p !== paper))} />
        ))}
        {papers.length === 0 && (
          <Typography variant="caption" color="text.secondary">No papers added.</Typography>
        )}
      </Box>
    </Box>
  );
};

const LevelEditor: React.FC<{ levels: SubCategoryLevel[]; onChange: (next: SubCategoryLevel[]) => void }> = ({ levels, onChange }) => {
  const [draftLevelName, setDraftLevelName] = useState('');

  const addLevel = () => {
    const trimmed = draftLevelName.trim();
    if (!trimmed || levels.some(l => l.name === trimmed)) {
      setDraftLevelName('');
      return;
    }
    onChange([...levels, { name: trimmed, papers: [] }]);
    setDraftLevelName('');
  };

  const updateLevel = (index: number, next: SubCategoryLevel) => {
    const copy = [...levels];
    copy[index] = next;
    onChange(copy);
  };

  const removeLevel = (index: number) => {
    onChange(levels.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Add Level (e.g. Foundation Level)"
          value={draftLevelName}
          onChange={(e) => setDraftLevelName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addLevel();
            }
          }}
        />
        <Button variant="outlined" size="small" onClick={addLevel}>Add Level</Button>
      </Box>
      {levels.map((level, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Level Name"
              value={level.name}
              onChange={(e) => updateLevel(index, { ...level, name: e.target.value })}
            />
            <IconButton size="small" color="error" onClick={() => removeLevel(index)} aria-label="Remove level">
              <Delete fontSize="small" />
            </IconButton>
          </Box>
          <PaperListEditor papers={level.papers} onChange={(papers) => updateLevel(index, { ...level, papers })} />
        </Paper>
      ))}
    </Box>
  );
};

const SubCategoryEditor: React.FC<{ value: SubCategoryItem[]; onChange: (next: SubCategoryItem[]) => void }> = ({ value, onChange }) => {
  const [draftName, setDraftName] = useState('');

  const addSubCategory = () => {
    const trimmed = draftName.trim();
    if (!trimmed || value.some(sc => sc.name === trimmed)) {
      setDraftName('');
      return;
    }
    onChange([...value, { name: trimmed, levels: [] }]);
    setDraftName('');
  };

  const updateSubCategory = (index: number, next: SubCategoryItem) => {
    const copy = [...value];
    copy[index] = next;
    onChange(copy);
  };

  const removeSubCategory = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Add Subcategory"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addSubCategory();
            }
          }}
        />
        <Button variant="outlined" onClick={addSubCategory}>Add</Button>
      </Box>
      {value.length === 0 && (
        <Typography variant="caption" color="text.secondary">No subcategories added yet.</Typography>
      )}
      {value.map((sc, index) => (
        <Accordion key={index} disableGutters sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography sx={{ flexGrow: 1 }}>{sc.name || '(unnamed)'}</Typography>
              {sc.levels.length > 0 && (
                <Chip size="small" color="primary" variant="outlined" label={`${sc.levels.length} level${sc.levels.length === 1 ? '' : 's'}`} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                label="Subcategory Name"
                value={sc.name}
                onChange={(e) => updateSubCategory(index, { ...sc, name: e.target.value })}
              />
              <IconButton color="error" onClick={() => removeSubCategory(index)} aria-label="Remove subcategory">
                <Delete fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Optional: add exam/study levels each with their own papers (e.g. CPA-style "Foundation Level" with individual exam
              papers). Leave empty for a standard flat subcategory with the usual Study Level / Mode of Study dropdowns.
            </Typography>
            <LevelEditor levels={sc.levels} onChange={(levels) => updateSubCategory(index, { ...sc, levels })} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const ServiceCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<FormState>(emptyForm);

  const [editTarget, setEditTarget] = useState<ServiceCategoryRecord | null>(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<FormState>(emptyForm);

  const [deleteTarget, setDeleteTarget] = useState<ServiceCategoryRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiGet<any>('/service-categories');
      setCategories(res?.data || []);
    } catch (error) {
      console.error('Error loading service categories:', error);
      setErrorMessage('Failed to load service categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const buildPayload = (form: FormState) => ({
    title: form.title.trim(),
    category: form.category.trim(),
    description: form.description.trim(),
    color: form.color.trim() || '#2196f3',
    iconKey: form.iconKey || 'star',
    subCategories: form.subCategories,
    requiresStudyDetails: form.requiresStudyDetails,
    requiresExperience: form.requiresExperience,
    amount: parseFloat(form.amount) || 0,
    currency: form.currency.trim() || 'RWF',
    commissionPercentage: parseFloat(form.commissionPercentage) || 0,
    isActive: form.isActive,
  });

  const validateForm = (form: FormState): string | null => {
    if (!form.title.trim() || !form.category.trim()) return 'Title and category are required';
    const amount = parseFloat(form.amount);
    if (form.amount !== '' && (isNaN(amount) || amount < 0)) return 'Please enter a valid amount';
    const commissionPercentage = parseFloat(form.commissionPercentage);
    if (form.commissionPercentage !== '' && (isNaN(commissionPercentage) || commissionPercentage < 0 || commissionPercentage > 100)) {
      return 'Commission percentage must be between 0 and 100';
    }
    return null;
  };

  const handleCreate = async () => {
    const validationError = validateForm(formData);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setCreating(true);
    try {
      await apiPost('/service-categories', buildPayload(formData));
      setSuccessMessage('Service category created successfully');
      setCreateOpen(false);
      setFormData(emptyForm);
      loadData();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to create service category');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (sc: ServiceCategoryRecord) => {
    setEditTarget(sc);
    setEditFormData({
      title: sc.title,
      category: sc.category,
      description: sc.description || '',
      color: sc.color || '#2196f3',
      iconKey: sc.iconKey || 'star',
      subCategories: sc.subCategories || [],
      requiresStudyDetails: sc.requiresStudyDetails !== false,
      requiresExperience: sc.requiresExperience !== false,
      amount: String(sc.amount),
      currency: sc.currency || 'RWF',
      commissionPercentage: String(sc.commissionPercentage),
      isActive: sc.isActive,
    });
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    const validationError = validateForm(editFormData);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setEditing(true);
    try {
      await apiPut(`/service-categories/${editTarget._id}`, buildPayload(editFormData));
      setSuccessMessage('Service category updated successfully');
      setEditTarget(null);
      loadData();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to update service category');
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/service-categories/${deleteTarget._id}`);
      setSuccessMessage('Service category deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting service category:', error);
      setErrorMessage('Failed to delete service category');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      const res = await apiDelete<any>('/service-categories');
      setSuccessMessage(res?.message || 'All service categories deleted');
      setDeleteAllOpen(false);
      loadData();
    } catch (error) {
      console.error('Error deleting all service categories:', error);
      setErrorMessage('Failed to delete all service categories');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleImportDefaults = async () => {
    const existingTitles = new Set(categories.map(c => c.title));
    const toImport = DEFAULT_CATEGORIES.filter(c => !existingTitles.has(c.title));
    if (toImport.length === 0) {
      setSuccessMessage('All default categories already exist');
      return;
    }
    setImporting(true);
    let imported = 0;
    let failed = 0;
    for (const category of toImport) {
      try {
        await apiPost('/service-categories', {
          ...category,
          amount: 0,
          currency: 'RWF',
          commissionPercentage: 0,
          isActive: true,
        });
        imported += 1;
      } catch (error) {
        console.error(`Error importing category "${category.title}":`, error);
        failed += 1;
      }
    }
    setImporting(false);
    if (imported > 0) {
      setSuccessMessage(`Imported ${imported} service categor${imported === 1 ? 'y' : 'ies'}. Set their price and commission below.${failed > 0 ? ` (${failed} failed)` : ''}`);
    } else {
      setErrorMessage('Failed to import default categories');
    }
    loadData();
  };

  const renderFormFields = (form: FormState, setForm: React.Dispatch<React.SetStateAction<FormState>>) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
      <TextField
        fullWidth
        label="Title"
        helperText="Shown to focal persons and used as the service name at registration"
        value={form.title}
        onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
      />
      <TextField
        fullWidth
        label="Category"
        helperText="Grouping label (e.g. 'Technology', 'Business')"
        value={form.category}
        onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
      />
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Description"
        value={form.description}
        onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Icon</InputLabel>
          <Select
            label="Icon"
            value={form.iconKey}
            onChange={(e: SelectChangeEvent) => setForm(prev => ({ ...prev, iconKey: e.target.value }))}
          >
            {ICON_OPTIONS.map(opt => {
              const OptIcon = opt.Icon;
              return (
                <MenuItem key={opt.key} value={opt.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OptIcon fontSize="small" />
                    {opt.label}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <TextField
          label="Color"
          type="color"
          value={form.color}
          onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
          sx={{ width: 120 }}
        />
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Subcategories</Typography>
        <SubCategoryEditor
          value={form.subCategories}
          onChange={(next) => setForm(prev => ({ ...prev, subCategories: next }))}
        />
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={form.requiresStudyDetails}
            onChange={(e) => setForm(prev => ({ ...prev, requiresStudyDetails: e.target.checked }))}
          />
        }
        label="Requires study level / mode of study fields at registration"
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.requiresExperience}
            onChange={(e) => setForm(prev => ({ ...prev, requiresExperience: e.target.checked }))}
          />
        }
        label="Requires relevant experience field at registration"
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="Amount"
          type="number"
          value={form.amount}
          onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
        />
        <TextField
          label="Currency"
          value={form.currency}
          onChange={(e) => setForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
          sx={{ width: 120 }}
        />
      </Box>
      <TextField
        fullWidth
        label="Commission Percentage"
        type="number"
        value={form.commissionPercentage}
        onChange={(e) => setForm(prev => ({ ...prev, commissionPercentage: e.target.value }))}
        helperText={
          commissionPreview(form.amount, form.commissionPercentage) !== null
            ? `Focal person earns ${form.currency || 'RWF'} ${commissionPreview(form.amount, form.commissionPercentage)} per paid registration`
            : undefined
        }
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.isActive}
            onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
          />
        }
        label="Active"
      />
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AttachMoney color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Service Categories
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={handleImportDefaults}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Import Default Categories'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteAllOpen(true)}
            disabled={categories.length === 0}
          >
            Delete All
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData(emptyForm); setCreateOpen(true); }}>
            Add Service Category
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage every service the platform offers: name, description, icon, subcategories (optionally with nested study levels and
        exam papers, e.g. CPA-style tracks), whether study details are required at registration, the amount a customer pays, and
        the commission percentage a focal person earns when a registration in that category is marked as paid.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' } }}>
                <TableCell>Service</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Subcategories</TableCell>
                <TableCell>Customer Pays</TableCell>
                <TableCell>Commission %</TableCell>
                <TableCell>Commission Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map(sc => {
                const CategoryIcon = iconForKey(sc.iconKey);
                const levelCount = (sc.subCategories || []).reduce((sum, item) => sum + (item.levels?.length || 0), 0);
                return (
                  <TableRow key={sc._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: sc.color, bgcolor: `${sc.color}15`, p: 0.5, borderRadius: 1, display: 'flex' }}>
                          <CategoryIcon />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{sc.title}</Typography>
                          {sc.description && (
                            <Typography variant="caption" color="text.secondary">{sc.description}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{sc.category}</TableCell>
                    <TableCell>
                      {sc.subCategories?.length || 0}
                      {levelCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {levelCount} level{levelCount === 1 ? '' : 's'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{sc.currency} {sc.amount.toFixed(2)}</TableCell>
                    <TableCell>{sc.commissionPercentage}%</TableCell>
                    <TableCell>{sc.currency} {((sc.amount * sc.commissionPercentage) / 100).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={sc.isActive ? 'Active' : 'Inactive'}
                        color={sc.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(sc)} aria-label="Edit service category">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(sc)} aria-label="Delete service category">
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      No service categories yet. Click "Import Default Categories" to load the standard set, or "Add Service Category" to create your own.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Add Service Category
          <IconButton onClick={() => setCreateOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {renderFormFields(formData, setFormData)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Edit Service Category
          <IconButton onClick={() => setEditTarget(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {renderFormFields(editFormData, setEditFormData)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Service Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteAllOpen} onClose={() => setDeleteAllOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete All Service Categories</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete all <strong>{categories.length}</strong> service categor{categories.length === 1 ? 'y' : 'ies'}?
            This cannot be undone. Focal persons will no longer be able to register customers for any service until you add or
            re-import categories.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteAll} disabled={deletingAll}>
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!successMessage} autoHideDuration={5000} onClose={() => setSuccessMessage(null)}>
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} autoHideDuration={5000} onClose={() => setErrorMessage(null)}>
        <Alert severity="error" onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default ServiceCategoriesPage;
